import React, { useCallback } from 'react';
import { BookOpen, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

import { useAgentKnowledgeBasesQuery, useUnlinkKnowledgeBaseMutation } from '@/query/agent.query';

import { KnowledgeBaseRow } from './knowledge-base-row';
import { SelectKnowledgeBaseContent } from './select-knowledge-base-dialog';

import useModalStore from '@/store/modal.store';

type AgentKnowledgeBaseTabProps = {
  agentId: number;
};

export const AgentKnowledgeBaseTab = React.memo(function AgentKnowledgeBaseTab({
  agentId,
}: AgentKnowledgeBaseTabProps) {
  const { data: knowledgeBases = [], isLoading } = useAgentKnowledgeBasesQuery(agentId, true, {
    enabled: !!agentId,
  });

  const unlinkMutation = useUnlinkKnowledgeBaseMutation(agentId);

  const handleUnlink = useCallback((kbId: number) => unlinkMutation.mutate(kbId), [unlinkMutation]);

  const handleAddKnowledgeBase = useCallback(() => {
    useModalStore.getState().openModal(() => <SelectKnowledgeBaseContent agentId={agentId} />);
  }, [agentId]);

  return (
    <Card className="bg-muted rounded-2xl gap-0 p-1 border-0 shadow-none">
      <CardHeader className="px-5 py-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-sm font-medium mb-1">Knowledge base</CardTitle>
            <p className="text-sm text-muted-foreground">
              Add knowledge base to provide context to the agent.
            </p>
          </div>
          <Button type="button" variant="outline" onClick={handleAddKnowledgeBase}>
            <BookOpen className="h-4 w-4" />
            Add knowledge base
          </Button>
        </div>
      </CardHeader>

      <CardContent className="bg-card rounded-xl shadow-sm p-5">
        <div className="space-y-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              <span className="text-sm text-muted-foreground ml-2">Loading knowledge bases...</span>
            </div>
          ) : knowledgeBases.length === 0 ? (
            <div className="text-center py-8">
              <BookOpen className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No knowledge bases linked yet.</p>
              <p className="text-xs text-muted-foreground mt-1">
                Link a knowledge base to give the agent additional context.
              </p>
            </div>
          ) : (
            knowledgeBases.map(kb => (
              <KnowledgeBaseRow
                key={kb.id}
                knowledgeBase={kb}
                onUnlink={handleUnlink}
                isUnlinking={unlinkMutation.isPending}
              />
            ))
          )}
        </div>

        {knowledgeBases.length > 0 && !isLoading && (
          <p className="text-sm text-muted-foreground mt-4">
            Your knowledge base is small enough to be included directly in the prompt for faster
            responses. We don't recommend RAG for small knowledge bases.
          </p>
        )}
      </CardContent>
    </Card>
  );
});
